import React, { useEffect } from 'react';

// Components
import GrowthLegend from './GrowthLegend';
import GrowthAnnualizedROILegend from './GrowthAnnualizedROILegend';
import SeriesLegend, { SeriesLegendProps } from '../SeriesLegend/SeriesLegend';
import { BenchmarksDisclaimer, HypotheticalReturnDisclaimer, InstrumentCustomDisclaimer, ShortHistoryPortfoliosDisclaimer } from '../../disclaimers/comparison';
import { GenericEmptyState } from '../../misc';

// Libraries
import classNames from 'classnames';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

// Types
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';

// Utils
import { COMPARISON_ITEM_TYPES, TIME_PERIODS } from '@/utils/constants';
import { DISCLAIMER_TAG } from '@/utils/disclaimers/constants';
import { getDateRangeFromQuickRange } from '@/utils/disclaimers/comparison';
import { formatMessage, FormattedMessage, LOCALE } from '@/utils/i18n';
import { LABEL_FORMATTERS } from '@/utils/charts';

type Props = {
  chartOptions: Highcharts.Options;
  disClaimerClassName?: string;
  disclaimer?: React.ReactNode | string;
  items?: ComparisonItem[];
  interval?: any;
  legend?: boolean;
  locale?: Locale;
  annualizedROILegend?: boolean;
  showBaseCurrencies?: boolean;
  targetName?: string;
  targetROI?: number;
  dateQuickRange?: Nullable<TIME_PERIODS>;
  shortHistory?: boolean;
  customDisclaimer?: boolean;
  dark?: boolean;
  seriesLegendProps?: SeriesLegendProps;
  startDate?: Nullable<Date>;
  endDate?: Date;
};

const GrowthChart: React.FC<Props> = ({
  chartOptions,
  disClaimerClassName,
  disclaimer,
  items = [],
  interval = null,
  legend = false,
  locale,
  annualizedROILegend = false,
  shortHistory = false,
  showBaseCurrencies = false,
  targetName,
  targetROI,
  dateQuickRange,
  customDisclaimer = false,
  dark = false,
  seriesLegendProps,
  startDate,
  endDate,
}) => {
  // To update language settings, use Highcharts.setOptions({ lang: ... }) before chart creation.
  // Reference: https://api.highcharts.com/highcharts/lang
  useEffect(() => {
    setLangLocale();
  }, [locale]);

  const chartHasData = chartOptions && chartOptions.series && chartOptions.series.length > 0;
  const showFooterDisclaimer = chartOptions.series && Object.values(chartOptions.series).some(serie => serie.data.length > 0);
  const comparisonHasBenchmarks = items.some(item => item.type === COMPARISON_ITEM_TYPES.BENCHMARK);

  const { startDate: calculatedStartDate, endDate: calculatedEndDate } = getDateRangeFromQuickRange(dateQuickRange, startDate, endDate, items, true);

  const setLangLocale = () => {
    const currentOptions = Highcharts.getOptions();
    Highcharts.setOptions({
      lang: {
        ...currentOptions.lang,
        ...LABEL_FORMATTERS.lang(locale || LOCALE.EN_CA),
      },
    });
  };

  return (
    <div className={classNames('growth-chart chart', { dark, 'no-item': !items.length })}>
      {legend && <GrowthLegend chartOptions={chartOptions} sort={false} showBaseCurrencies={showBaseCurrencies} />}
      {annualizedROILegend && <GrowthAnnualizedROILegend targetName={targetName} targetROI={targetROI} items={items} dateQuickRange={dateQuickRange ?? null} />}
      <div className="graph-container">
        {chartHasData && (
          <HighchartsReact
            className="highchart"
            containerProps={{ className: 'highcharts-react-container' }}
            highcharts={Highcharts}
            options={chartOptions}
            constructorType="stockChart"
          />
        )}

        {!chartHasData && items.length > 0 && (
          <div className={classNames('empty-chart flex-fill', { 'is-light': !dark })}>
            <FormattedMessage id="Charts.EmptyPeriod" />
          </div>
        )}

        {!items.length && !chartHasData && (
          <GenericEmptyState dark={dark} header={formatMessage('Charts.NoItem.Title')} body={formatMessage('Charts.NoItem.Body')} />
        )}

        {showFooterDisclaimer && chartOptions.series && (
          <>
            <SeriesLegend {...seriesLegendProps} />
            <div className="chart-disclaimer">
              {items.length > 0 && (
                <HypotheticalReturnDisclaimer
                  items={items}
                  series={chartOptions.series.reduce((acc, serie) => acc.concat(serie.data), [])}
                  interval={interval}
                />
              )}
              {comparisonHasBenchmarks && <BenchmarksDisclaimer items={items} />}
              {shortHistory && <ShortHistoryPortfoliosDisclaimer items={items} startDate={calculatedStartDate} endDate={calculatedEndDate} />}
              {disclaimer && <div className={disClaimerClassName}>{disclaimer}</div>}
              {customDisclaimer && (
                // Custom disclaimers on charts are only used in V1 proposals, so it's safe to target
                // only disclaimers tagged with PROPOSAL. Should this assumption change, then this logic
                // should be revisited.
                <InstrumentCustomDisclaimer items={items} disclaimerTags={[DISCLAIMER_TAG.PROPOSAL]} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GrowthChart;
